import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mayAssignWork, type Department, type MessageableUser } from '@nbh/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';

interface AssigneeFieldProps {
  department: Department;
  value: string | undefined;
  onChange: (assigneeId: string | undefined) => void;
}

/**
 * Optionally names who will do the work, turning a report into a directed job (D34).
 *
 * Renders nothing at all for the roles that may not dispatch, which is most of them.
 * A disabled control would be worse than absent here: it would tell a housekeeper that
 * assigning is a thing they are failing to be allowed to do, when reporting the fault is
 * the whole of their job and is complete without it.
 *
 * Left blank the ticket goes to the department pool as claimable work — the first-come
 * path (D31) — which stays the default even for a dispatcher, because it is nearly
 * always the better one.
 */
export function AssigneeField({ department, value, onChange }: AssigneeFieldProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [staff, setStaff] = useState<MessageableUser[]>([]);

  const mayDispatch = mayAssignWork(user?.role);

  useEffect(() => {
    if (!mayDispatch) return;
    // Silent on failure: this control is an enhancement over the department pool, and
    // an unreachable directory must not stop a fault being reported.
    api
      .messageDirectory()
      .then(setStaff)
      .catch(() => undefined);
  }, [mayDispatch]);

  if (!mayDispatch) return null;

  // Scoped to the department the report is being filed against, so the list is the
  // people who could actually take it rather than the whole property.
  const candidates = staff.filter((person) => person.department === department);

  return (
    <label className="block">
      <span className="mb-1 block text-sm text-slate-400">{t('report.assignee')}</span>
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || undefined)}
        className="min-h-[48px] w-full rounded-xl border border-slate-700 bg-slate-900 px-3 text-base text-slate-100"
      >
        <option value="">{t('report.assigneeUnclaimed')}</option>
        {candidates.map((person) => (
          <option key={person.id} value={person.id}>
            {person.name}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-slate-500">{t('report.assigneeHint')}</span>
    </label>
  );
}
